import ModelViewer from "components/ModelViewer";
import React, { useCallback, useEffect, useState } from "react";
import "./ModelViewerContainer.scss";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { isEmpty } from "lodash";
import { isShoeProduct } from "utils/meshUtils";
import {
  CAMERA_MODE as CAMERA_MODE_DEFAULT,
  CAMERA_TYPE,
} from "configs/RendererConfig";
import Button from "components/Button";
import { TextField } from "@mui/material";
const mapStateToProps = ({ avatarReducer = {} }) => ({
  product: get(avatarReducer, "product", {}),
  avatarAssets: get(avatarReducer, "avatarAssets", null),
  userProfile: get(avatarReducer, "userProfile", {}),
  isDefaultAvatar: get(avatarReducer, "isDefaultAvatar", false),
  defaultUserProfile: get(avatarReducer, "defaultUserProfile"),
});

const getCameraPosition = (cameraMode) => {
  return {
    cameraTarget: cameraMode.modelTarget,
    cameraOrbit: cameraMode.modelOrbit,
    fieldOfView: cameraMode.modelFov,
    maxCameraOrbit: cameraMode.maxCameraOrbit,
  };
};

export const getDefaultEnvironment = (selectedProduct, selectedSkybox) => {
  return !isEmpty(selectedSkybox)
    ? selectedSkybox.environment
    : isShoeProduct(selectedProduct)
    ? ""
    : "/environment/pillars_1k.hdr";
};

export const getDefaultSkybox = (selectedProduct, selectedSkybox) => {
  return !isEmpty(selectedSkybox)
    ? selectedSkybox.skybox
    : selectedProduct.brand !== "revolve"
    ? ""
    : "/environment/pillars_1k.hdr";
};

const ModelViewerContainer = ({
  showHotspots,
  selectedProduct,
  cameraMode,
  setCameraMode,
  setSceneLoading,
  sceneLoading,
  selectedSkybox,
}) => {
  const {
    avatarAssets = null,
    userProfile = {},
    defaultUserProfile = {},
    isDefaultAvatar,
  } = useSelector(mapStateToProps);

  const [selectedScene, setSelectedScene] = useState(null);
  const [isScalingActive, setIsScalingActive] = useState(false);
  const [scale, setScale] = useState(
    !isEmpty(avatarAssets.scalingFactor)
      ? avatarAssets.scalingFactor
      : { x: 1.0, y: 1.0, z: 1.0 }
  );
  let CAMERA_MODE =
    CAMERA_MODE_DEFAULT[selectedProduct.brand] ?? CAMERA_MODE_DEFAULT;

  const resetCamera = useCallback(() => {
    setCameraMode({ ...CAMERA_MODE[CAMERA_TYPE.BODY] });
  }, [CAMERA_MODE, setCameraMode]);

  useEffect(() => {
    const createHotspots = (product) => {
      const hotspots = (product.hotspots ?? []).map((hotspot) => {
        const {
          userAvatar: { bustShape, bustPosition },
        } = isDefaultAvatar ? defaultUserProfile : userProfile;
        let key =
          bustShape && bustPosition ? `${bustShape}_${bustPosition}` : "";
        let { id } = hotspot;
        const {
          normal = "",
          modelOrbit,
          modelPosition,
        } = hotspot[key] || hotspot["default"] || {};
        const updatedHotspot = {
          slot: "hotspot-" + id,
          annotation: hotspot.messages?.[0] ?? "",
          position: modelPosition,
          normal,
          orbit: modelOrbit,
        };
        return updatedHotspot;
      });
      return hotspots;
    };
    if (!isEmpty(avatarAssets)) {
      if (isShoeProduct(selectedProduct)) {
        const src = avatarAssets.assets["shoeUrl"].split("?")[0];
        setSelectedScene({
          src,
          additionalAssets: null,
          hotspots: [],
        });
      } else {
        const showNewBody = selectedProduct.brand === "fef";
        const src = showNewBody
          ? "https://d1pu2jrxdqoaw5.cloudfront.net/female_VS.glb"
          : avatarAssets.avatarUrl.split("?")[0];
        const additionalAssets = JSON.stringify(
          Object.values(showNewBody ? [] : avatarAssets.assets)
            .filter((asset) => !!asset)
            .map((asset) => ({
              url: asset.split("?")[0],
              position: { x: 0, y: 0, z: 0 },
            }))
        );
        setSceneLoading(true);
        const hotspots = createHotspots(selectedProduct);
        setCameraMode(CAMERA_MODE[CAMERA_TYPE.BODY]);
        setSelectedScene({
          src,
          additionalAssets,
          hotspots,
        });
      }
      // console.log(sources);
    }
  }, [
    defaultUserProfile,
    isDefaultAvatar,
    selectedProduct,
    userProfile,
    avatarAssets,
    setCameraMode,
    CAMERA_MODE,
  ]);

  const showScaling = false;
  // typeof window !== "undefined" &&
  // (window.location.origin === "https://qa.dopplr.digital" ||
  //   window.location.origin === "http://localhost:3000") &&
  // typeof window !== "undefined" &&
  // (window.location.pathname === "/dopplr-plugin/revolve" ||
  //   window.location.pathname === "/dopplr-plugin/indianterrain");

  const handleScale = (e, key) => {
    const value = parseFloat(e?.target?.value ?? 1) || 1;
    setScale((prev) => ({ ...prev, [key]: value }));
  };

  const openScaling = async () => {
    setIsScalingActive((prev) => !prev);
  };

  const closeScaling = async () => {
    setIsScalingActive((prev) => !prev);
  };

  const getModelViewerConfigProps = () => {
    if (selectedProduct?.modelViewerConfig) {
      const {
        exposure = 1,
        environmentImage,
        skyboxImage,
        toneMapping = "aces",
      } = selectedProduct.modelViewerConfig;
      return {
        exposure,
        environmentImage: environmentImage
          ? environmentImage
          : getDefaultEnvironment(selectedProduct, selectedSkybox),
        skyboxImage: skyboxImage
          ? skyboxImage
          : getDefaultSkybox(selectedProduct, selectedSkybox),
        toneMapping,
      };
    }
    return {
      exposure: "1",
      environmentImage: getDefaultEnvironment(selectedProduct, selectedSkybox),
      skyboxImage: getDefaultSkybox(selectedProduct, selectedSkybox),
      toneMapping: "aces",
    };
  };

  if (!selectedScene) {
    return null;
  }

  return (
    <div className="ModelViewer__Container">
      <ModelViewer
        config={selectedScene || {}}
        setLoading={setSceneLoading}
        loading={sceneLoading}
        showHotspots={showHotspots}
        selectedProduct={selectedProduct}
        scale={scale}
        selectedSkybox={selectedSkybox}
        // cameraTarget={
        //   isShoeProduct(selectedProduct) ? "0.01m 0.05m 0m" : "0.01m 1.25m 0m"
        // }
        cameraPosition={getCameraPosition(cameraMode)}
        exposure={isShoeProduct(selectedProduct) ? "0.9" : "1"}
        resetCamera={resetCamera}
        {...getModelViewerConfigProps()}
      />
      {showScaling && (
        <div className="DopplrScene__Scale">
          {isScalingActive ? (
            <>
              <TextField
                label={"X"}
                size="small"
                className="Measurements__TextField"
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    marginRight: "1em",
                    marginBottom: "0.5em",
                    borderRadius: "1em",
                  },
                  ".MuiFormHelperText-root.Mui-error": {
                    position: "absolute",
                    bottom: "-20px",
                  },
                }}
                type="number"
                inputProps={{
                  step: "0.01",
                }}
                value={scale.x || "1.00"}
                onChange={(e) => handleScale(e, "x")}
              />
              <TextField
                label={"Y"}
                size="small"
                step="0.01"
                className="Measurements__TextField"
                inputProps={{
                  step: "0.01",
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    marginRight: "1em",
                    marginBottom: "0.5em",
                    borderRadius: "1em",
                  },
                  ".MuiFormHelperText-root.Mui-error": {
                    position: "absolute",
                    bottom: "-20px",
                  },
                }}
                type="number"
                value={scale.y || "1.00"}
                onChange={(e) => handleScale(e, "y")}
              />
              <TextField
                label={"Z"}
                size="small"
                step="0.01"
                className="Measurements__TextField"
                inputProps={{
                  step: "0.01",
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    marginRight: "1em",
                    marginBottom: "0.5em",
                    borderRadius: "1em",
                  },
                  ".MuiFormHelperText-root.Mui-error": {
                    position: "absolute",
                    bottom: "-20px",
                  },
                }}
                type="number"
                value={scale.z || "1.00"}
                onChange={(e) => handleScale(e, "z")}
              />
              <Button onClick={closeScaling}>Scale</Button>
            </>
          ) : (
            <Button onClick={openScaling}>Scale</Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ModelViewerContainer;
